import {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';

import {formatDateTimeWithMs, stringToColor} from '~/modules/utils';
import {useGetAiToolTraceDetailQuery} from '~/store/rtkQuery';
import {AISupport} from '~/modules/api';

const TraceEntryRow = ({entry}: {entry: AISupport.AIToolTraceEntryRow}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-600';
      case 'warn':
        return 'text-yellow-600';
      case 'info':
        return 'text-blue-600';
      case 'finish':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const toolColor = stringToColor(entry.tool_name);

  const formatJson = (jsonString: string | null) => {
    if (!jsonString) return null;
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  return (
    <div className="border-b border-base-300 last:border-b-0">
      <div
        className="p-4 hover:bg-base-200 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3">
          {/* Fixed-width log level */}
          <div className="flex-none w-12">
            <span className={`text-xs font-semibold uppercase ${getLogLevelColor(entry.log_level)}`}>
              {entry.log_level}
            </span>
          </div>

          {/* Main content area */}
          <div className="flex-grow min-w-0">
            {/* Timestamp - top left */}
            <div className="text-xs text-gray-500 font-mono mb-1">
              {formatDateTimeWithMs(entry.timestamp)}
            </div>

            {/* Tool name with colored badge */}
            <div className="flex items-center gap-2 mb-2">
              <span
                className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-white"
                style={{backgroundColor: toolColor}}
              >
                {entry.tool_name}
              </span>
            </div>

            {/* Message */}
            <p className="text-sm break-words">{entry.message}</p>
          </div>

          {/* Expand icon */}
          <div className="flex-none">
            <svg
              className={`w-5 h-5 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && (entry.data || entry.metadata) && (
        <div className="px-4 pb-4 bg-base-100">
          {entry.metadata && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">Metadata:</h4>
              <pre className="bg-base-300 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">
                {formatJson(entry.metadata)}
              </pre>
            </div>
          )}
          {entry.data && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Data:</h4>
              <pre className="bg-base-300 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">
                {formatJson(entry.data)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AiToolTraceDetailScene = () => {
  const {traceId} = useParams<{traceId: string}>();
  const navigate = useNavigate();

  const {data, isLoading, error} = useGetAiToolTraceDetailQuery({
    traceId: traceId!,
  });

  if (error) {
    return (
      <div className="p-4">
        <button className="btn btn-ghost mb-4" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <div className="alert alert-error">
          <span>Error loading trace: {(error as any).message}</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <button className="btn btn-ghost mb-4" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <div className="flex justify-center p-8">
          <span className="loading loading-dots loading-lg"></span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4">
        <button className="btn btn-ghost mb-4" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <div className="alert alert-warning">
          <span>Trace not found</span>
        </div>
      </div>
    );
  }

  const errorCount = data.entries.filter(e => e.log_level === 'error').length;
  const startTimestamp = data.entries[0]?.timestamp;
  const endTimestamp = data.entries[data.entries.length - 1]?.timestamp;
  const duration = startTimestamp && endTimestamp
    ? new Date(endTimestamp).getTime() - new Date(startTimestamp).getTime()
    : 0;

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <button
        className="btn btn-ghost mb-4"
        onClick={() => navigate('/ai-support/ai-tool-traces')}
      >
        &larr; Back to Traces
      </button>

      {/* Header Card */}
      <div className="card bg-base-200 mb-6">
        <div className="card-body">
          <h1 className="text-3xl font-bold mb-4">Trace Detail</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Trace Name</p>
              <p className="font-mono font-semibold">{data.traceName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Trace ID</p>
              <p className="font-mono text-sm">{data.traceId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Entries</p>
              <p className="font-semibold">{data.entries.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Start Time</p>
              <p className="text-sm font-mono">{formatDateTimeWithMs(startTimestamp)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">End Time</p>
              <p className="text-sm font-mono">{formatDateTimeWithMs(endTimestamp)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Duration</p>
              <p className="text-sm">{(duration / 1000).toFixed(2)}s</p>
            </div>
            {errorCount > 0 && (
              <div>
                <p className="text-sm text-gray-500">Errors</p>
                <span className="badge badge-error">{errorCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Entries List */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body p-0">
          <div className="p-4 border-b border-base-300">
            <h2 className="text-xl font-semibold">Trace Entries</h2>
            <p className="text-sm text-gray-500">
              Click on an entry to expand and view details
            </p>
          </div>
          <div>
            {data.entries.map((entry, index) => (
              <TraceEntryRow key={index} entry={entry} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
